import {
  Camera,
  Download,
  File,
  FileCheck,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Image as ImageIcon,
  Loader2,
  MoreVertical,
  Plus,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useFileUpload } from '../../hooks/useFileUpload';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FileDropzone } from '../file-dropzone';
import { VisuallyHidden } from '../visually-hidden';

export type JobAttachmentCategory = 'proof_of_work' | 'before' | 'after' | 'document';

export interface JobAttachmentsSectionProps {
  jobId: number;
  isReadOnly?: boolean;
  className?: string;
  defaultCategory?: JobAttachmentCategory | 'all';
}

const CATEGORY_META: Record<
  JobAttachmentCategory,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    colorClass: string;
    badgeClass: string;
  }
> = {
  proof_of_work: {
    label: 'Proof of Work / Signature',
    icon: FileCheck,
    colorClass: 'text-emerald-600 dark:text-emerald-400',
    badgeClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  },
  before: {
    label: 'Before Photo',
    icon: Camera,
    colorClass: 'text-amber-600 dark:text-amber-400',
    badgeClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
  },
  after: {
    label: 'After Photo',
    icon: Sparkles,
    colorClass: 'text-blue-600 dark:text-blue-400',
    badgeClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
  },
  document: {
    label: 'Document / Plan',
    icon: FileText,
    colorClass: 'text-slate-600 dark:text-slate-400',
    badgeClass: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30',
  },
};

function formatBytesCompact(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageMime(mime: string, fileName: string): boolean {
  if (mime.startsWith('image/')) return true;
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext ? ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'heic'].includes(ext) : false;
}

export function JobAttachmentsSection({
  jobId,
  isReadOnly = false,
  className,
  defaultCategory = 'all',
}: JobAttachmentsSectionProps) {
  const [selectedFilterCategory, setSelectedFilterCategory] = useState<
    JobAttachmentCategory | 'all'
  >(defaultCategory);
  const [uploadCategory, setUploadCategory] = useState<JobAttachmentCategory>('proof_of_work');
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [lightboxFile, setLightboxFile] = useState<{
    id: number;
    fileName: string;
    url?: string;
    category?: string | null;
  } | null>(null);

  const utils = trpc.useUtils();
  const { data: files = [], isLoading } = trpc.files.getForTarget.useQuery(
    { targetType: 'job', targetId: jobId },
    { enabled: jobId > 0 }
  );

  const { mutateAsync: createAssociation } = trpc.files.createAssociation.useMutation();
  const { mutateAsync: dissociate } = trpc.files.dissociate.useMutation();
  const { mutateAsync: updateFile } = trpc.files.update.useMutation();
  const { uploadAll, isUploading } = useFileUpload();

  const invalidate = () =>
    utils.files.getForTarget.invalidate({ targetType: 'job', targetId: jobId });

  const handleUpload = async (droppedFiles: File[]) => {
    if (droppedFiles.length === 0) return;

    const promise = (async () => {
      const fileIds = await uploadAll(droppedFiles, uploadCategory);
      if (fileIds.length === 0) return;

      await Promise.all(
        fileIds.map((fileId) =>
          createAssociation({
            fileId,
            jobId,
            category: uploadCategory,
          })
        )
      );
      await invalidate();
    })();

    toast.promise(promise, {
      loading: `Uploading ${droppedFiles.length} file(s)...`,
      success: 'Files & photos attached successfully',
      error: 'Failed to upload attachments',
    });
  };

  const handleRemove = async (associationId: number) => {
    try {
      await dissociate({ id: associationId });
      await invalidate();
      toast.success('Attachment removed');
    } catch {
      toast.error('Failed to remove attachment');
    }
  };

  const handleSaveRename = async (fileId: number) => {
    if (!editLabel.trim()) {
      setEditingFileId(null);
      return;
    }
    try {
      await updateFile({ id: fileId, label: editLabel.trim() });
      setEditingFileId(null);
      await invalidate();
      toast.success('Attachment label updated');
    } catch {
      toast.error('Failed to update label');
    }
  };

  // Group files by category for counters
  const categoryCounts = useMemo(() => {
    const counts = {
      all: files.length,
      proof_of_work: 0,
      before: 0,
      after: 0,
      document: 0,
    };
    for (const f of files) {
      const cat = (f.category as JobAttachmentCategory) || 'document';
      if (counts[cat] !== undefined) {
        counts[cat]++;
      } else {
        counts.document++;
      }
    }
    return counts;
  }, [files]);

  const filteredFiles = useMemo(() => {
    if (selectedFilterCategory === 'all') return files;
    return files.filter((f) => {
      const cat = f.category || 'document';
      return cat === selectedFilterCategory;
    });
  }, [files, selectedFilterCategory]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* ── Category Filter Pills ──────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5 pb-1 border-b border-border/40">
        <Button
          variant={selectedFilterCategory === 'all' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSelectedFilterCategory('all')}
          className="h-7 text-xs px-2.5 rounded-lg gap-1.5"
        >
          <FolderOpen className="h-3.5 w-3.5" />
          <span>All Photos & Docs</span>
          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
            {categoryCounts.all}
          </Badge>
        </Button>

        <Button
          variant={selectedFilterCategory === 'proof_of_work' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSelectedFilterCategory('proof_of_work')}
          className="h-7 text-xs px-2.5 rounded-lg gap-1.5"
        >
          <FileCheck className="h-3.5 w-3.5 text-emerald-500" />
          <span>Proof of Work</span>
          {categoryCounts.proof_of_work > 0 && (
            <Badge
              variant="outline"
              className="text-[10px] px-1 py-0 h-4 border-emerald-500/30 text-emerald-600"
            >
              {categoryCounts.proof_of_work}
            </Badge>
          )}
        </Button>

        <Button
          variant={selectedFilterCategory === 'before' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSelectedFilterCategory('before')}
          className="h-7 text-xs px-2.5 rounded-lg gap-1.5"
        >
          <Camera className="h-3.5 w-3.5 text-amber-500" />
          <span>Before</span>
          {categoryCounts.before > 0 && (
            <Badge
              variant="outline"
              className="text-[10px] px-1 py-0 h-4 border-amber-500/30 text-amber-600"
            >
              {categoryCounts.before}
            </Badge>
          )}
        </Button>

        <Button
          variant={selectedFilterCategory === 'after' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSelectedFilterCategory('after')}
          className="h-7 text-xs px-2.5 rounded-lg gap-1.5"
        >
          <Sparkles className="h-3.5 w-3.5 text-blue-500" />
          <span>After</span>
          {categoryCounts.after > 0 && (
            <Badge
              variant="outline"
              className="text-[10px] px-1 py-0 h-4 border-blue-500/30 text-blue-600"
            >
              {categoryCounts.after}
            </Badge>
          )}
        </Button>

        <Button
          variant={selectedFilterCategory === 'document' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSelectedFilterCategory('document')}
          className="h-7 text-xs px-2.5 rounded-lg gap-1.5"
        >
          <FileText className="h-3.5 w-3.5 text-slate-500" />
          <span>Documents</span>
          {categoryCounts.document > 0 && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
              {categoryCounts.document}
            </Badge>
          )}
        </Button>
      </div>

      {/* ── Upload Area (Dropzone & Category Selector) ─────────────── */}
      {!isReadOnly && (
        <div className="space-y-2 bg-muted/20 border border-dashed border-border/70 rounded-xl p-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5 text-primary" />
              <span>Attach Photos / Proof of Work</span>
            </span>

            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">Upload As:</span>
              <Select
                value={uploadCategory}
                onValueChange={(val) => setUploadCategory(val as JobAttachmentCategory)}
              >
                <SelectTrigger className="h-7 text-xs w-48 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="proof_of_work">
                    <div className="flex items-center gap-1.5">
                      <FileCheck className="h-3.5 w-3.5 text-emerald-500" />
                      <span>Proof of Work / Docket</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="before">
                    <div className="flex items-center gap-1.5">
                      <Camera className="h-3.5 w-3.5 text-amber-500" />
                      <span>Before Photo</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="after">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-blue-500" />
                      <span>After Photo</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="document">
                    <div className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-slate-500" />
                      <span>General Document</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <FileDropzone
            onFilesDrop={handleUpload}
            noClick={false}
            className="p-4 text-center hover:bg-background/80 transition-all cursor-pointer rounded-lg border border-border/40 bg-background/50"
          >
            <div className="flex flex-col items-center justify-center gap-1.5 py-1">
              {isUploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              ) : (
                <UploadCloud className="h-6 w-6 text-primary/70" />
              )}
              <div className="space-y-0.5">
                <p className="text-xs font-medium text-foreground">
                  {isUploading ? 'Uploading file...' : 'Drop completion photos or click to browse'}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Supports PNG, JPG, PDF, SVG, DOCX up to your workspace storage quota
                </p>
              </div>
            </div>
          </FileDropzone>
        </div>
      )}

      {/* ── Files & Photo Gallery Grid ─────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="p-8 text-center rounded-xl border border-border/40 bg-muted/10 space-y-1.5">
          <FolderOpen className="h-8 w-8 text-muted-foreground/50 mx-auto" />
          <p className="text-xs font-medium text-foreground">No attachments in this view</p>
          <p className="text-[11px] text-muted-foreground">
            {selectedFilterCategory === 'all'
              ? 'Upload before/after photos or proof-of-work dockets above.'
              : `No attachments categorized as ${selectedFilterCategory.replace(/_/g, ' ')}.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {filteredFiles.map((file) => {
            const isImage = isImageMime(file.fileType, file.fileName);
            const category = (file.category as JobAttachmentCategory) || 'document';
            const meta = CATEGORY_META[category] || CATEGORY_META.document;
            const CategoryIcon = meta.icon;

            return (
              <div
                key={file.id}
                className="group relative flex flex-col bg-card border border-border/60 rounded-xl overflow-hidden shadow-xs hover:border-border hover:shadow-sm transition-all"
              >
                {/* Top Image Preview or File Header */}
                {isImage ? (
                  <div
                    onClick={() =>
                      setLightboxFile({
                        id: file.id,
                        fileName: file.label || file.fileName,
                        url: file.viewUrl,
                        category: file.category,
                      })
                    }
                    className="relative aspect-video bg-muted/40 overflow-hidden cursor-pointer flex items-center justify-center"
                  >
                    {file.viewUrl ? (
                      <img
                        src={file.viewUrl}
                        alt={file.label || file.fileName}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
                    )}

                    {/* Category Overlay Tag */}
                    <div className="absolute top-2 left-2">
                      <Badge
                        variant="secondary"
                        className={cn(
                          'text-[9px] px-1.5 py-0.5 backdrop-blur-md shadow-xs flex items-center gap-1',
                          meta.badgeClass
                        )}
                      >
                        <CategoryIcon className="h-2.5 w-2.5" />
                        <span>{meta.label.split(' ')[0]}</span>
                      </Badge>
                    </div>

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="text-[11px] font-semibold text-white bg-black/60 px-2 py-1 rounded-md backdrop-blur-xs">
                        View Photo
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-muted/20 border-b border-border/40 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        {file.fileType.includes('pdf') ? (
                          <FileText className="h-4 w-4 text-red-500" />
                        ) : file.fileType.includes('sheet') || file.fileType.includes('csv') ? (
                          <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <File className="h-4 w-4" />
                        )}
                      </div>
                      <Badge
                        variant="outline"
                        className={cn('text-[9px] px-1.5 py-0', meta.badgeClass)}
                      >
                        {meta.label}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Card Content & Metadata */}
                <div className="p-2.5 flex-1 flex flex-col justify-between gap-2">
                  <div>
                    {editingFileId === file.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename(file.id);
                            if (e.key === 'Escape') setEditingFileId(null);
                          }}
                          className="h-6 text-xs px-1.5"
                          autoFocus
                        />
                        <Button
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => handleSaveRename(file.id)}
                        >
                          Save
                        </Button>
                      </div>
                    ) : (
                      <p
                        className="text-xs font-semibold text-foreground truncate"
                        title={file.label || file.fileName}
                      >
                        {file.label || file.fileName}
                      </p>
                    )}

                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-1">
                      <span>{formatBytesCompact(file.fileSize)}</span>
                      <span>•</span>
                      <span>{new Date(file.createdAt * 1000).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="flex items-center justify-between pt-1 border-t border-border/30 text-xs">
                    {file.viewUrl ? (
                      <a
                        href={file.viewUrl}
                        target="_blank"
                        rel="noreferrer"
                        download={file.fileName}
                        className="text-[11px] text-primary hover:underline flex items-center gap-1 font-medium"
                      >
                        <Download className="h-3 w-3" />
                        <span>Download</span>
                      </a>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Uploading...</span>
                    )}

                    {!isReadOnly && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="text-xs">
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingFileId(file.id);
                              setEditLabel(file.label || file.fileName);
                            }}
                          >
                            Rename Label
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleRemove(file.associationId)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-3 w-3 mr-1.5" />
                            <span>Remove from Job</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Fullscreen Lightbox Modal ──────────────────────────────── */}
      <Dialog open={!!lightboxFile} onOpenChange={(open) => !open && setLightboxFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-card/95 backdrop-blur-md border border-border flex flex-col">
          <VisuallyHidden>
            <DialogTitle>{lightboxFile?.fileName || 'Photo View'}</DialogTitle>
            <DialogDescription>Full resolution view of {lightboxFile?.fileName}</DialogDescription>
          </VisuallyHidden>

          {/* Modal Header */}
          <div className="p-3.5 border-b border-border/40 flex items-center justify-between bg-muted/40">
            <div className="flex items-center gap-2 min-w-0">
              <ImageIcon className="h-4 w-4 text-primary shrink-0" />
              <span className="font-semibold text-xs text-foreground truncate">
                {lightboxFile?.fileName}
              </span>
              {lightboxFile?.category && (
                <Badge variant="outline" className="text-[9px] capitalize">
                  {lightboxFile.category.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              {lightboxFile?.url && (
                <Button variant="outline" size="sm" asChild className="h-7 text-xs gap-1.5">
                  <a
                    href={lightboxFile.url}
                    download={lightboxFile.fileName}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span>Download</span>
                  </a>
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLightboxFile(null)}
                className="h-7 w-7 rounded-lg"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Modal Image Body */}
          <div className="flex-1 min-h-[350px] p-4 flex items-center justify-center bg-black/5 overflow-auto">
            {lightboxFile?.url && (
              <img
                src={lightboxFile.url}
                alt={lightboxFile.fileName}
                className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-md"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
